/**
 * Los permisos de Conti Club Más que la interfaz mira.
 *
 * La lista buena es la del API (`transport/catalog/permissions.ts`): de ahí
 * salen los que se dan de alta en lala-ms, y el API valida cada herramienta
 * contra ellos. Aquí solo están los que deciden qué se ENSEÑA, con el mismo
 * texto, para que un nombre mal escrito no compile en vez de esconder un botón
 * a todo el mundo sin que nadie se entere.
 *
 * La interfaz esconde; el backend decide. Quitar un botón no protege nada.
 */
export const PERMISSIONS = {
  /** Entrar a la aplicación (§10.2). */
  access: "ccmas:global.view",
  /** Todo: satisface cualquier otro, incluido `access` (§10.2). */
  manage: "ccmas:global.manage",

  memoryWrite: "ccmas:memory.write",
  /** El consumo de tokens: la sección de Work y el contador de la cabecera. */
  usageRead: "ccmas:usage.read",
  /** Sincronizar o fijar los precios de los modelos. */
  usageWrite: "ccmas:usage.write",
  /** Ver el proceso de revisión de documentos y el seguimiento de cada uno. */
  invoiceReviewsRead: "ccmas:document_reviews.read",
  /** Digitar: tomar un documento, editar sus líneas y decidir. */
  invoiceReviewsDecide: "ccmas:document_reviews.digitate",
  /** Meter documentos al proceso de revisión y reprocesar los que fallaron. */
  invoiceReviewsRun: "ccmas:document_reviews.run",
  /** «Acierto»: lo que acierta el proceso de revisión y sus alertas. */
  accuracyRead: "ccmas:accuracy.read",
  /** Marcar alertas del acierto como atendidas. */
  accuracyManage: "ccmas:accuracy.manage",
  /** La auditoría (`get_activity`). Aún no la usa ninguna pantalla. */
  activityRead: "ccmas:activity.read",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Si `held` trae `name` sin importar mayúsculas: auth guarda y devuelve los
 * permisos y roles en mayúsculas (`CCMAS:GLOBAL.MANAGE`), y aquí se nombran
 * en minúsculas. Se compara así en vez de normalizar `/me`, para que valga
 * venga como venga.
 */
const includesIgnoringCase = (
  held: readonly string[],
  name: string,
): boolean => {
  const wanted = name.toLowerCase();
  return held.some((item) => item.toLowerCase() === wanted);
};

/**
 * Si quien tiene `held` puede `permission`. LA ÚNICA regla del front.
 *
 * `ccmas:global.manage` satisface cualquier permiso, igual que en el
 * chequeo del API: es una regla, no una lista, para que un permiso nuevo no se
 * quede sin él por olvido. `/me` devuelve los permisos tal cual, sin
 * expandir, así que la expansión vive aquí. Mayúsculas o minúsculas, da igual.
 */
export const hasPermission = (
  held: readonly string[],
  permission: Permission,
): boolean =>
  includesIgnoringCase(held, PERMISSIONS.manage) ||
  includesIgnoringCase(held, permission);

/** El rol de auth que ve los costos. No es un permiso: ver {@link canSeeCosts}. */
export const SUPER_ADMIN_ROLE = "SUPER_ADMIN";

/**
 * Si quien es `me` ve los costos: el costo y el consumo de IA, en dólares y
 * en tokens. LA EXCEPCIÓN a `hasPermission`, y la única.
 *
 * Va por el ROL y no por un permiso porque ningún permiso lo dice:
 * `global.manage` lo tienen también PROGRAM_ADMIN y BUSINESS_OWNER, y vale
 * por `usage.read`. Solo esconde: el API no cambia, y quien tenga
 * `usage.read` lo puede seguir pidiendo. El P.V.P. y la señal de precio de
 * las líneas son del negocio, no costos, y no pasan por aquí.
 */
export const canSeeCosts = (me: { readonly roles: readonly string[] }) =>
  includesIgnoringCase(me.roles, SUPER_ADMIN_ROLE);

/**
 * Si quien es `me` es el admin de Alfred: el que ve todos los pedidos,
 * cambia su estado y responde. Es el mismo rol que ve los costos, y como
 * aquel, solo decide lo que se DICE --«Todos los pedidos» o «Tus
 * pedidos»--: qué pedidos llegan y qué botones salen lo decide el API
 * (`list_alfred_tickets`, `can*` y `allowedTransitions` del detalle).
 */
export const isAlfredAdmin = (me: { readonly roles: readonly string[] }) =>
  includesIgnoringCase(me.roles, SUPER_ADMIN_ROLE);
